import firebase, { db } from 'utils/firebase';
import {
  AppId,
  AppSettings,
  SecurityPlus,
} from 'types/shared/documents/restaurants';
import {
  isReadableLayout,
  layoutForSave,
  PRODUCT_LAYOUT_FIELD,
  PRODUCT_LAYOUT_VERSION,
  ProductLayout,
  readProductLayout,
  StoredProductLayout,
  storedLayoutVersion,
} from './productLayout';
import { rightUpdates, RightsChanges } from './securityRights';

export type DashboardEdits = {
  restaurantId: string;
  uid: string | null;
  /** null: the layout was not changed. */
  layout: ProductLayout | null;
  /** Product ids this dashboard knows; others in the stored layout are kept. */
  knownIds: string[];
  rights: RightsChanges;
};

export type SaveDashboardEdits = (edits: DashboardEdits) => Promise<void>;

export class LayoutVersionError extends Error {
  constructor() {
    super(
      'Die Anordnung wurde mit einer neueren Version des Dashboards gespeichert. Bitte lade die Seite neu.',
    );
    this.name = 'LayoutVersionError';
  }
}

/**
 * Saves the layout field of restaurantData and the changed Security Plus
 * rights paths (published settings and, if present, the settings draft)
 * atomically. Everything else in these documents stays as it is.
 */
export async function saveDashboardEdits(
  { restaurantId, uid, layout, knownIds, rights }: DashboardEdits,
  now = Date.now(),
): Promise<void> {
  const changesRights = !!Object.keys(rights).length;
  if (!layout && !changesRights) return;

  const restaurantRef = db.collection('restaurantData').doc(restaurantId);
  const publishedRef = db
    .collection(`restaurants/${restaurantId}/apps`)
    .doc('security_plus');
  const draftRef = db
    .collection(`restaurants/${restaurantId}/appDrafts`)
    .doc('security_plus');

  await db.runTransaction(async (transaction) => {
    const restaurant = (await transaction.get(restaurantRef)).data() ?? {};
    const published = changesRights
      ? ((await transaction.get(publishedRef)).data() as
          | AppSettings<SecurityPlus>
          | undefined)
      : undefined;
    const draft = changesRights
      ? (await transaction.get(draftRef)).data()
      : undefined;

    const storedLayout = restaurant[PRODUCT_LAYOUT_FIELD];
    if (layout && (storedLayoutVersion(storedLayout) ?? 0) > PRODUCT_LAYOUT_VERSION) {
      throw new LayoutVersionError();
    }

    const updates = rightUpdates(
      published,
      (restaurant.apps ?? []) as AppId[],
      rights,
    );

    if (layout) {
      const storedLayoutObject = isReadableLayout(storedLayout)
        ? storedLayout
        : {};
      const stored: StoredProductLayout = {
        // Keeps fields another dashboard version stored alongside.
        ...storedLayoutObject,
        version: PRODUCT_LAYOUT_VERSION,
        ...layoutForSave(layout, readProductLayout(storedLayout), knownIds),
        updatedAt: now,
        updatedBy: uid,
      };
      transaction.update(restaurantRef, { [PRODUCT_LAYOUT_FIELD]: stored });
    }

    const draftHasSettings = !!draft?.settings && typeof draft.settings === 'object';
    for (const [right, holders] of Object.entries(updates)) {
      // Rights ids contain dots, so they need a FieldPath, not a dotted string.
      const path = new firebase.firestore.FieldPath('settings', 'rights', right);
      transaction.update(publishedRef, path, holders);
      if (draftHasSettings) transaction.update(draftRef, path, holders);
    }
  });
}
